import { supabase } from './supabase';
import { clearActiveSession } from './chat-storage';
import type { User as SupabaseUser } from '@supabase/supabase-js';

export type User = SupabaseUser;

export interface AuthError {
  message: string;
}

export const signUp = async (email: string, password: string): Promise<{ user: User | null; error: AuthError | null }> => {
  try {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: `${window.location.origin}/auth/callback`,
      },
    });

    if (error) {
      if (error.message.includes('User already registered')) {
        return { user: null, error: { message: 'user_already_exists' } };
      }
      return { user: null, error: { message: error.message } };
    }

    return { user: data.user, error: null };
  } catch (error) {
    console.error('Signup error:', error);
    return { user: null, error: { message: 'An unexpected error occurred' } };
  }
};

export const signIn = async (email: string, password: string): Promise<{ user: User | null; error: AuthError | null }> => {
  try {
    console.log('Attempting sign in for email:', email);
    
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });

    if (error) {
      console.error('Supabase sign-in error:', {
        message: error.message,
        code: error.status
      });

      if (error.message.includes('Invalid login credentials')) {
        return { user: null, error: { message: 'Invalid email or password' } };
      }

      if (error.message.includes('Email not confirmed')) {
        return { user: null, error: { message: 'Please confirm your email before signing in' } };
      }

      if (error.message.includes('Rate limit')) {
        return { user: null, error: { message: 'Too many sign in attempts. Please try again later' } };
      }

      return { user: null, error: { message: error.message } };
    }

    console.log('Sign in successful for user:', data.user?.id);
    return { user: data.user, error: null };
  } catch (error) {
    console.error('Unexpected sign in error:', error);
    return { user: null, error: { message: 'An unexpected error occurred' } };
  }
};

export const signOut = async (): Promise<{ error: AuthError | null }> => {
  try {
    const { error } = await supabase.auth.signOut();
    if (error) throw error;
    
    // Clear all chat-related data
    clearActiveSession();
    
    // Clear all local storage
    localStorage.clear();
    
    // Reload the page to ensure a clean state
    window.location.reload();
    
    return { error: null };
  } catch (error) {
    console.error('Signout error:', error);
    return { error: { message: 'Failed to sign out' } };
  }
};

export const updatePassword = async (newPassword: string): Promise<{ error: AuthError | null }> => {
  try {
    const { error } = await supabase.auth.updateUser({
      password: newPassword
    });

    if (error) {
      return { error: { message: error.message } };
    }

    return { error: null };
  } catch (error) {
    console.error('Update password error:', error);
    return { error: { message: 'Failed to update password' } };
  }
};

export const getCurrentUser = async (): Promise<User | null> => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    return user;
  } catch (error) {
    console.error('Get current user error:', error);
    return null;
  }
};